import { Film, Copy, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "@/i18n/useTranslation";
import { calculateScriptAverageScore, getParticipantStatus, getReviewStatusMetadata, getScoreColor } from "@/features/dashboard/utils";
import type { AvailableForm, Script, ScriptForm } from "@/features/dashboard/types";

type TranslateFn = ReturnType<typeof useTranslation>["t"];
type LanguageCode = ReturnType<typeof useTranslation>["language"];

interface ParticipantsViewProps {
  scripts: Script[];
  availableFormsByScript: Record<string, AvailableForm[]>;
  addingForm: { scriptId: string; formId: string } | null;
  formatDate: (dateString: string) => string;
  onCopy: (value: string, label?: string) => Promise<void>;
  onAddForm: (script: Script, formId: string) => Promise<void>;
  onShare: (script: Script, scriptForm: ScriptForm) => void;
  onManageParticipants: (script: Script, scriptForm: ScriptForm) => void;
  onDeleteScript: (scriptId: string) => Promise<void> | void;
}

const EmptyParticipantsState = ({ title, description }: { title: string; description: string }) => (
  <Card className="border border-border/80 bg-card/95 backdrop-blur-md shadow-sm rounded-2xl py-12 text-center max-w-7xl mx-auto">
    <CardContent>
      <Film className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
      <h3 className="mb-2 text-xl font-bold text-foreground">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </CardContent>
  </Card>
);

interface ScriptSummaryProps {
  script: Script;
  scriptAverageScore: number | null;
  formatDate: (dateString: string) => string;
  t: TranslateFn;
}

const ScriptSummary = ({ script, scriptAverageScore, formatDate, t }: ScriptSummaryProps) => (
  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-lg font-semibold text-foreground">{script.movie_name}</h3>
        {script.password_protected && (
          <Badge variant="secondary" className="text-xs">
            {t("dashboard.participants.badge_protected")}
          </Badge>
        )}
      </div>
      <p className="text-sm text-muted-foreground tabular-nums">
        {t("dashboard.participants.created", { date: formatDate(script.created_at) })}
      </p>
      {script.custom_message && (
        <p className="max-w-2xl text-sm text-muted-foreground leading-relaxed">{script.custom_message}</p>
      )}
    </div>
    <div className="text-right">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("dashboard.participants.avg_score")}
      </p>
      {typeof scriptAverageScore === "number" ? (
        <p className={`text-base font-bold tabular-nums ${getScoreColor(scriptAverageScore)}`}>
          {scriptAverageScore.toFixed(1)} / 5
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">{t("dashboard.participants.no_responses")}</p>
      )}
    </div>
  </div>
);

interface PasswordSectionProps {
  script: Script;
  onCopy: ParticipantsViewProps["onCopy"];
  t: TranslateFn;
}

const PasswordSection = ({ script, onCopy, t }: PasswordSectionProps) => {
  if (!script.password_protected || !script.review_password) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          void onCopy(script.review_password!, "Password");
        }}
      >
        <Copy className="mr-2 h-3 w-3" />
        {t("dashboard.participants.copy_password")}
      </Button>
    </div>
  );
};

interface ParticipantListProps {
  participants: ReturnType<typeof getParticipantStatus>["participants"];
  t: TranslateFn;
}

const ParticipantList = ({ participants, t }: ParticipantListProps) => {
  if (participants.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("dashboard.participants.no_participants")}</p>;
  }

  return (
    <div className="space-y-2">
      {participants.map((participant) => (
        <div key={participant.key} className="flex items-center justify-between text-sm">
          <span className="font-medium">{participant.contactName || participant.email}</span>
          <span
            className={`rounded px-2 py-1 text-xs ${
              participant.status === "submitted"
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {participant.status === "submitted"
              ? t("dashboard.participants.participant_status_submitted")
              : t("dashboard.participants.participant_status_pending")}
          </span>
        </div>
      ))}
    </div>
  );
};

interface AddFormDropdownProps {
  script: Script;
  formsToAdd: AvailableForm[];
  language: LanguageCode;
  addingForm: ParticipantsViewProps["addingForm"];
  onAddForm: ParticipantsViewProps["onAddForm"];
  t: TranslateFn;
}

const AddFormDropdown = ({ script, formsToAdd, language, addingForm, onAddForm, t }: AddFormDropdownProps) => {
  if (formsToAdd.length === 0) {
    return null;
  }

  return (
    <div className="border-border border-t border-dashed pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full md:w-auto"
            disabled={addingForm !== null}
          >
            <Plus className="mr-2 h-3 w-3" />
            {t("dashboard.participants.add_more_forms")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {formsToAdd.map((form) => {
            const isAddingThisForm = addingForm?.scriptId === script.id && addingForm?.formId === form.id;
            const displayName = language === "th" && form.name_th ? form.name_th : form.name;
            const displayDescription =
              language === "th" && form.description_th ? form.description_th : form.description;

            return (
              <DropdownMenuItem
                key={form.id}
                className="cursor-pointer"
                disabled={addingForm !== null}
                onClick={() => {
                  void onAddForm(script, form.id);
                }}
              >
                <div className="flex flex-col items-start">
                  <span className="font-medium">{displayName}</span>
                  {displayDescription && (
                    <span className="line-clamp-1 text-xs text-muted-foreground">{displayDescription}</span>
                  )}
                </div>
                {isAddingThisForm && (
                  <div className="ml-auto h-3 w-3 animate-spin rounded-full border border-current border-t-transparent" />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

interface DeleteScriptActionProps {
  script: Script;
  onDeleteScript: ParticipantsViewProps["onDeleteScript"];
  t: TranslateFn;
}

const DeleteScriptAction = ({ script, onDeleteScript, t }: DeleteScriptActionProps) => (
  <div className="flex justify-end border-border border-t border-dashed pt-3">
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
          <Trash2 className="mr-2 h-4 w-4" />
          {t("dashboard.participants.delete_button")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("dashboard.participants.delete_title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("dashboard.participants.delete_description", { script: script.movie_name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              void onDeleteScript(script.id);
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {t("dashboard.participants.confirm_delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
);

interface ScriptFormItemProps {
  script: Script;
  scriptForm: ScriptForm;
  language: LanguageCode;
  t: TranslateFn;
  onCopy: ParticipantsViewProps["onCopy"];
  onShare: ParticipantsViewProps["onShare"];
  onManageParticipants: ParticipantsViewProps["onManageParticipants"];
  formsToAdd: AvailableForm[];
  addingForm: ParticipantsViewProps["addingForm"];
  onAddForm: ParticipantsViewProps["onAddForm"];
  onDeleteScript: ParticipantsViewProps["onDeleteScript"];
}

const ScriptFormItem = ({
  script,
  scriptForm,
  language,
  t,
  onCopy,
  onShare,
  onManageParticipants,
  formsToAdd,
  addingForm,
  onAddForm,
  onDeleteScript,
}: ScriptFormItemProps) => {
  const { participants: participantStatuses } = getParticipantStatus(scriptForm);
  const statusMetadata = getReviewStatusMetadata(scriptForm);
  const statusLabel = statusMetadata?.label;
  const totalParticipants = statusMetadata?.totalParticipants ?? 0;
  const submittedCount = statusMetadata?.completedParticipants ?? 0;
  const reviewLinkLabel = t("dashboard.participants.review_link_label");
  const participantsLabel = t("dashboard.participants.participants_label");
  const displayName =
    language === "th" && scriptForm.feedback_forms.name_th
      ? scriptForm.feedback_forms.name_th
      : scriptForm.feedback_forms.name;

  const statusAriaLabel = statusLabel
    ? `${statusLabel} (${submittedCount}/${totalParticipants})`
    : undefined;

  const submissionSummary = t("dashboard.participants.submission_summary", {
    completed: submittedCount,
    total: totalParticipants,
    label: t("dashboard.participants.participant_status_submitted"),
  });

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-foreground">{displayName}</h4>
          {statusMetadata && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} aria-label={statusAriaLabel} className={`inline-flex h-3.5 w-3.5 shrink-0 rounded-full ${statusMetadata.color}`} />
              </TooltipTrigger>
              <TooltipContent>{statusLabel}</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {submissionSummary}
          </Badge>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onShare(script, scriptForm)}>
              {t("dashboard.participants.share")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onManageParticipants(script, scriptForm)}>
              {t("dashboard.participants.manage.button")}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{reviewLinkLabel}</p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="flex-1 break-all rounded bg-muted px-2 py-1 text-xs">{scriptForm.review_link}</code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void onCopy(scriptForm.review_link, "Review link");
              }}
            >
              <Copy className="mr-2 h-3 w-3" />
              {t("dashboard.participants.copy_link")}
            </Button>
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{participantsLabel}</p>
          <ParticipantList participants={participantStatuses} t={t} />
        </div>
      </div>

      <AddFormDropdown
        script={script}
        formsToAdd={formsToAdd}
        language={language}
        addingForm={addingForm}
        onAddForm={onAddForm}
        t={t}
      />

      <DeleteScriptAction script={script} onDeleteScript={onDeleteScript} t={t} />
    </div>
  );
};

interface ScriptFormListProps {
  script: Script;
  formsToAdd: AvailableForm[];
  language: LanguageCode;
  t: TranslateFn;
  onCopy: ParticipantsViewProps["onCopy"];
  onShare: ParticipantsViewProps["onShare"];
  onManageParticipants: ParticipantsViewProps["onManageParticipants"];
  onAddForm: ParticipantsViewProps["onAddForm"];
  onDeleteScript: ParticipantsViewProps["onDeleteScript"];
  addingForm: ParticipantsViewProps["addingForm"];
}

const ScriptFormList = ({
  script,
  formsToAdd,
  language,
  t,
  onCopy,
  onShare,
  onManageParticipants,
  onAddForm,
  onDeleteScript,
  addingForm,
}: ScriptFormListProps) => {
  if (script.script_forms.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("dashboard.participants.no_forms")}</p>;
  }

  return script.script_forms.map((scriptForm) => (
    <ScriptFormItem
      key={scriptForm.id}
      script={script}
      scriptForm={scriptForm}
      language={language}
      t={t}
      onCopy={onCopy}
      onShare={onShare}
      onManageParticipants={onManageParticipants}
      formsToAdd={formsToAdd}
      addingForm={addingForm}
      onAddForm={onAddForm}
      onDeleteScript={onDeleteScript}
    />
  ));
};

interface ScriptParticipantsCardProps {
  script: Script;
  availableForms: AvailableForm[];
  addingForm: ParticipantsViewProps["addingForm"];
  formatDate: ParticipantsViewProps["formatDate"];
  onCopy: ParticipantsViewProps["onCopy"];
  onShare: ParticipantsViewProps["onShare"];
  onManageParticipants: ParticipantsViewProps["onManageParticipants"];
  onAddForm: ParticipantsViewProps["onAddForm"];
  onDeleteScript: ParticipantsViewProps["onDeleteScript"];
  t: TranslateFn;
  language: LanguageCode;
}

const ScriptParticipantsCard = ({
  script,
  availableForms,
  addingForm,
  formatDate,
  onCopy,
  onShare,
  onManageParticipants,
  onAddForm,
  onDeleteScript,
  t,
  language,
}: ScriptParticipantsCardProps) => {
  const scriptAverageScore = calculateScriptAverageScore(script);

  return (
    <Card className="border border-border/80 bg-card/95 backdrop-blur-md shadow-sm rounded-2xl overflow-hidden">
      <CardContent className="space-y-5 p-6">
        <ScriptSummary script={script} scriptAverageScore={scriptAverageScore} formatDate={formatDate} t={t} />
        <PasswordSection script={script} onCopy={onCopy} t={t} />
        <div className="space-y-4">
          <ScriptFormList
            script={script}
            formsToAdd={availableForms}
            language={language}
            t={t}
            onCopy={onCopy}
            onShare={onShare}
            onManageParticipants={onManageParticipants}
            onAddForm={onAddForm}
            onDeleteScript={onDeleteScript}
            addingForm={addingForm}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export const ParticipantsView = ({
  scripts,
  availableFormsByScript,
  addingForm,
  formatDate,
  onCopy,
  onAddForm,
  onShare,
  onManageParticipants,
  onDeleteScript,
}: ParticipantsViewProps) => {
  const { t, language } = useTranslation();

  if (scripts.length === 0) {
    return (
      <EmptyParticipantsState
        title={t("dashboard.participants.empty_title")}
        description={t("dashboard.participants.empty_description")}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
      {scripts.map((script) => (
        <ScriptParticipantsCard
          key={script.id}
          script={script}
          availableForms={availableFormsByScript[script.id] ?? []}
          addingForm={addingForm}
          formatDate={formatDate}
          onCopy={onCopy}
          onShare={onShare}
          onManageParticipants={onManageParticipants}
          onAddForm={onAddForm}
          onDeleteScript={onDeleteScript}
          t={t}
          language={language}
        />
      ))}
    </div>
  );
};
